export const LOCAL_STORAGE_CHUNK_LOADING_KEY = 'chunk_loading_refresh_key';

const CHUNK_LOAD_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Loading (CSS )?chunk [\w-]+ failed/i,
  /missing: .*\.chunk\.(js|css)/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Failed to load module script/i,
  /Unable to preload CSS for/i,
  /dynamically imported module/i,
];

const CHUNK_RESOURCE_PATTERNS = [
  /\/assets\/.*\.(js|css)(\?|$)/i,
  /\/static\/(js|css)\/.*\.(chunk\.)?(js|css)(\?|$)/i,
];

function gettext(message) {
  return window.django?.gettext ? window.django.gettext(message) : message;
}

function interpolate(message, data) {
  return window.django?.interpolate ? window.django.interpolate(message, data, true) : message.replace('%(version)s', data.version);
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function getErrorParts(error) {
  if (!error) return { textParts: [], resourceParts: [] };
  if (typeof error === 'string') {
    return {
      textParts: [error],
      resourceParts: [],
    };
  }

  const reason = error.reason || error.error || error;
  const target = error.target || reason.target || reason.srcElement;
  const resourceParts = [
    reason.request,
    target?.src,
    target?.href,
  ].filter(Boolean);

  return {
    textParts: [
    reason.name,
    reason.message,
    reason.type,
    reason.request,
    error.message,
    error.filename,
    ...resourceParts,
  ].filter(Boolean),
    resourceParts,
  };
}

export function isChunkLoadError(error) {
  const { textParts, resourceParts } = getErrorParts(error);
  const errorText = textParts.join(' ');
  if (!errorText) return false;

  return (
    CHUNK_LOAD_ERROR_PATTERNS.some(pattern => pattern.test(errorText))
    || (
      resourceParts.length > 0
      && CHUNK_RESOURCE_PATTERNS.some(pattern => pattern.test(resourceParts.join(' ')))
    )
  );
}

export function shouldRefreshAfterChunkLoadError() {
  const lastRefresh = window.localStorage.getItem(LOCAL_STORAGE_CHUNK_LOADING_KEY);
  const lastRefreshTime = lastRefresh ? new Date(lastRefresh).getTime() : null;
  const now = Date.now();

  if (!lastRefreshTime || Number.isNaN(lastRefreshTime) || lastRefreshTime < now - 60 * 1000) {
    window.localStorage.setItem(LOCAL_STORAGE_CHUNK_LOADING_KEY, new Date(now).toISOString());
    return true;
  }
  return false;
}

function reloadWithCacheBust() {
  const url = new URL(window.location.href);
  url.searchParams.set('__clouderp_reload', Date.now().toString());
  window.location.replace(url.toString());
}

function showChunkLoadErrorFallback(version) {
  const container = document.getElementById('root');
  if (!container) return;

  const versionText = version
    ? ` ${interpolate(gettext('This might be because a new version (%(version)s) of the application is available!'), { version })}`
    : '';
  const title = escapeHtml(gettext('New version available!'));
  const description = escapeHtml(`${gettext('One or more of the application files can not be loaded.')}${versionText}`);
  const reloadText = escapeHtml(gettext('Reload now'));

  container.innerHTML = `
    <div style="align-items:center;box-sizing:border-box;display:flex;min-height:100vh;justify-content:center;padding:24px;text-align:center;">
      <div style="max-width:420px;">
        <div style="font-size:64px;line-height:1;margin-bottom:16px;">!</div>
        <h1 style="font-family:Arial,sans-serif;font-size:24px;font-weight:500;margin:0 0 12px;">${title}</h1>
        <p style="font-family:Arial,sans-serif;font-size:16px;line-height:1.5;margin:0 0 24px;">
          ${description}
        </p>
        <button id="clouderp-chunk-load-reload" style="background:#1976d2;border:0;border-radius:4px;color:#fff;cursor:pointer;font-family:Arial,sans-serif;font-size:14px;padding:10px 18px;">
          ${reloadText}
        </button>
      </div>
    </div>
  `;

  document.getElementById('clouderp-chunk-load-reload')?.addEventListener('click', () => {
    window.localStorage.removeItem(LOCAL_STORAGE_CHUNK_LOADING_KEY);
    reloadWithCacheBust();
  });
}

export async function handleChunkLoadError(error, options = {}) {
  if (!isChunkLoadError(error)) return false;

  const {
    setVersionError,
    setError,
    showFallback = false,
    reload = true,
  } = options;

  let version = null;
  try {
    const response = await fetch('/release/');
    ({ version } = await response.json());
    if (setVersionError) setVersionError(version);
  } catch (fetchError) {
    if (setError) setError(fetchError);
  }

  if (reload && shouldRefreshAfterChunkLoadError()) {
    reloadWithCacheBust();
    return true;
  }

  if (showFallback) {
    showChunkLoadErrorFallback(version);
  }

  return true;
}

export function installChunkLoadErrorHandlers() {
  if (window.__clouderpChunkLoadErrorHandlerInstalled) return;
  window.__clouderpChunkLoadErrorHandlerInstalled = true;

  window.addEventListener('unhandledrejection', (event) => {
    if (!isChunkLoadError(event.reason)) return;

    event.preventDefault();
    handleChunkLoadError(event.reason, { showFallback: true });
  });

  window.addEventListener(
    'error',
    (event) => {
      if (!isChunkLoadError(event)) return;

      event.preventDefault();
      handleChunkLoadError(event, { showFallback: true });
    },
    true,
  );
}
